import { Injectable } from '@angular/core';
import {
  IPaginatedResult,
  ISapDataService,
  ISort,
  mapODataFilters,
  ODataFilter,
  ODataOperator,
  rethrowAsSapError,
  SapError,
} from 'src/app/lego-utils';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Material } from 'src/app/models';
import { MaterialSearchAdapter } from 'src/app/models/domain/adapters';
import { MaterialSearchSetService } from 'src/generated-clients/material';

@Injectable({
  providedIn: 'root',
})
export class MaterialSearchDataService implements ISapDataService<Material, string> {
  private readonly descriptions = new Map<string, string>();

  public constructor(
    private readonly materialSearchSetService: MaterialSearchSetService,
    private readonly materialSearchAdapter: MaterialSearchAdapter
  ) {}

  public update(
    _id: string,
    _entity: Partial<Material>,
    _config?: { skipWrite?: boolean | undefined }
  ): Observable<Material> {
    throw new Error('Method not implemented.');
  }

  public getSingle(id: string): Observable<Material> {
    return this.materialSearchSetService
      .materialSearchSetMaterialNumberGet(id, this.materialSearchAdapter.$select)
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d) {
            throw new SapError(`Unknown error`);
          }

          return this.materialSearchAdapter.adaptToDomain(response.d);
        })
      );
  }

  public getMultiple(filters?: ODataFilter): Observable<Material[]> {
    return this.materialSearchSetService
      .materialSearchSetGet(
        mapODataFilters(filters, '2'),
        undefined,
        this.materialSearchAdapter.$select
      )
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d) {
            throw new SapError(`Unknown error`);
          }

          return (
            response.d.results?.map((materialData) =>
              this.materialSearchAdapter.adaptToDomain(materialData)
            ) ?? []
          );
        })
      );
  }

  public getPage(
    _page: number,
    _perPage: number,
    _sort?: Array<ISort<Material>>,
    _filters?: ODataFilter,
    _expand?: string[]
  ): Observable<IPaginatedResult<Material>> {
    throw new Error('Method not implemented.');
  }

  public getMaterialDescriptions(materialNumbers: string[]): Observable<Map<string, string>> {
    const result = materialNumbers.reduce((acc, materialNumber) => {
      const description = this.descriptions.get(materialNumber);

      if (description) {
        acc.set(materialNumber, description);
      }

      return acc;
    }, new Map<string, string>());

    const materialsToGet = materialNumbers.filter((x) => !result.has(x));

    if (!materialsToGet.length) {
      return of(result);
    }

    const filters: ODataFilter = {
      key: 'MaterialNumber',
      operator: ODataOperator.Equals,
      value: materialsToGet,
    };

    return this.materialSearchSetService
      .materialSearchSetGet(mapODataFilters(filters, '2'), undefined, [
        'MaterialNumber',
        'MaterialDescrUpper',
      ])
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d) {
            throw new SapError(`Unknown error`);
          }

          const responseMaterials =
            response.d.results?.map((materialData) =>
              this.materialSearchAdapter.adaptToDomain(materialData)
            ) ?? [];

          responseMaterials.forEach((x) => {
            this.descriptions.set(x.materialNumber, x.materialDescription);
            result.set(x.materialNumber, x.materialDescription);
          });

          return result;
        })
      );
  }

  public create(_entity: Partial<Material>): Observable<Material> {
    throw new Error('Method not implemented.');
  }

  public delete(_id: string, _options?: unknown): Observable<void> {
    throw new Error('Method not implemented.');
  }
}
